import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  constructor() {}

  /**
   * Valida si un token JWT está expirado
   * @param token Token JWT a validar
   * @returns true si está expirado, false si es válido
   */
  isTokenExpired(token: string): boolean {
    if (!token) {
      return true;
    }

    try {
      // Decodificar el payload del JWT
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Verificar si el token ha expirado
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error al decodificar token:', error);
      return true; // Si no se puede decodificar, considerarlo expirado
    }
  }

  /**
   * Obtiene el tiempo restante del token en segundos
   * @param token Token JWT
   * @returns Tiempo restante en segundos, 0 si está expirado
   */
  getTokenTimeRemaining(token: string): number {
    if (!token) {
      return 0;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const timeRemaining = payload.exp - currentTime;

      return Math.max(0, timeRemaining);
    } catch (error) {
      console.error('Error al obtener tiempo restante del token:', error);
      return 0;
    }
  }

  /**
   * Obtiene la información del usuario del token
   * @param token Token JWT
   * @returns Información del usuario o null si no se puede decodificar
   */
  getTokenUserInfo(token: string): any {
    if (!token) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        userId: payload.sub || payload.userId,
        username: payload.username,
        role: payload.role,
        exp: payload.exp,
        iat: payload.iat,
      };
    } catch (error) {
      console.error(
        'Error al obtener información del usuario del token:',
        error
      );
      return null;
    }
  }

  /**
   * Id numérico del usuario según el JWT vigente (claim `userId`).
   *
   * Es la fuente de verdad para identificar al usuario: viene firmado por el
   * backend, a diferencia de `localStorage.user`, que varios efectos y
   * servicios reescriben (user.effects.ts, sede-user.service.ts) y puede
   * quedar desfasado durante la sesión.
   *
   * @returns el id, o null si no hay token válido
   */
  getCurrentUserId(): number | null {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    // No se comprueba la expiración: el claim sigue identificando al mismo
    // usuario y la renovación la maneja el interceptor.
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const id = Number(payload.userId);
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch (error) {
      console.error('Error al obtener el id de usuario del token:', error);
      return null;
    }
  }

  /**
   * Rol del usuario en mayúsculas y sin el prefijo `ROLE_`.
   *
   * El backend Java firma el claim `roles` con las authorities de Spring
   * Security, que llevan prefijo: `ROLE_ADMIN` (ver SecurityUtils.convertToAuthority).
   * Si vienen varias, van separadas por coma. Se normaliza todo aquí para que
   * los componentes puedan comparar contra 'ADMIN', 'SUPERADMIN', etc.
   *
   * Si el token no trae el claim se cae al usuario de localStorage: el rol no
   * es una frontera de seguridad (el backend lo revalida contra la BD), solo
   * decide qué se muestra.
   *
   * @returns el rol normalizado, o null si no se pudo determinar
   */
  getCurrentUserRole(): string | null {
    const normalizar = (valor: any): string | null => {
      if (!valor) return null;
      const roles = String(valor)
        .split(',')
        .map((r) => r.trim().toUpperCase().replace(/^ROLE_/, ''))
        .filter((r) => !!r);
      return roles.length ? roles[0] : null;
    };

    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const desdeToken = normalizar(payload.roles ?? payload.role);
        if (desdeToken) return desdeToken;
      } catch (error) {
        console.error('Error al obtener el rol del usuario del token:', error);
      }
    }

    try {
      const raw = localStorage.getItem('user');
      return raw ? normalizar(JSON.parse(raw).role) : null;
    } catch {
      return null;
    }
  }

  /**
   * Verifica si el token necesita renovación (expira en menos de 5 minutos)
   * @param token Token JWT
   * @returns true si necesita renovación
   */
  needsRefresh(token: string): boolean {
    const timeRemaining = this.getTokenTimeRemaining(token);
    const fiveMinutes = 5 * 60; // 5 minutos en segundos

    return timeRemaining > 0 && timeRemaining < fiveMinutes;
  }

  /**
   * Limpia todos los datos de autenticación del localStorage
   */
  clearAuthData(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('coordinador');
    localStorage.removeItem('asesores');
    localStorage.removeItem('userStatus');
    sessionStorage.clear();
  }

  /**
   * Valida y limpia el token si está expirado
   * @returns true si el token es válido, false si fue limpiado
   */
  validateAndCleanToken(): boolean {
    const token = localStorage.getItem('token');

    if (!token) {
      return false;
    }

    if (this.isTokenExpired(token)) {
      console.warn('🔒 Token expirado detectado, limpiando sesión...');
      this.clearAuthData();
      return false;
    }

    return true;
  }

  /**
   * Verifica si hay datos de sesión válidos
   * @returns true si hay token y usuario, false en caso contrario
   */
  hasValidSession(): boolean {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    return !!(token && user && !this.isTokenExpired(token));
  }

  /**
   * Limpia la sesión sin mostrar modal (para casos donde no hay sesión activa)
   */
  silentLogout(): void {
    console.log('🔒 Limpiando sesión silenciosamente...');
    this.clearAuthData();
  }
}
